import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Sparkles, HeartPulse, Dumbbell, GraduationCap, Car, Building2, Briefcase, LayoutGrid,
  ArrowRight, ArrowLeft, Globe,
} from 'lucide-react';
import OnboardingStepper from '@/components/onboarding/OnboardingStepper';

// Sprint 40 — steps map 1:1 onto the first 3 stages of the shared journey
// (see onboardingStages.js). Step 4 ("Todo listo", a static transition
// screen) was removed: it added a full extra click with nothing actionable
// on it, right before dropping the tenant into a second wizard that used to
// restart its own "Paso 1 de 4" counter. Completing step 3 now saves and
// navigates straight into /tenant/setup, which continues the SAME shared
// stepper at "WhatsApp" instead of feeling like a new product.
const STEP_KEYS = { 1: 'negocio', 2: 'detalles', 3: 'sede' };

const VERTICAL_ICONS = {
  beauty: Sparkles, health: HeartPulse, fitness: Dumbbell,
  education: GraduationCap, automotive: Car, real_estate: Building2,
  professional_services: Briefcase, generic: LayoutGrid,
};

const TIMEZONES = [
  'America/Mexico_City', 'America/Monterrey', 'America/Cancun', 'America/Tijuana',
  'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Buenos_Aires',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'Europe/Madrid', 'UTC',
];

export default function OnboardingWizard() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [verticals, setVerticals] = useState([]);
  const [verticalFields, setVerticalFields] = useState([]);

  // Step 1: Vertical + basic info
  const [vertical, setVertical] = useState('');
  const [subtype, setSubtype] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [country, setCountry] = useState('Mexico');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [currency, setCurrency] = useState('MXN');
  const [description, setDescription] = useState('');

  // Step 2: Business details. RFC and Sitio web moved out of the mandatory
  // flow (Sprint 40) — a business doesn't need either to start operating,
  // and both remain editable in Mi Negocio > Configuración whenever the
  // tenant is ready to fill them in.
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [verticalConfig, setVerticalConfig] = useState({});

  // Step 3: First location
  const [locName, setLocName] = useState('');
  const [locAddress, setLocAddress] = useState('');
  const [locTimezone, setLocTimezone] = useState('');
  const [locPhone, setLocPhone] = useState('');
  const [locEmail, setLocEmail] = useState('');

  useEffect(() => {
    api.get('/tenant/profile').then(r => {
      const p = r.data;
      setProfile(p);
      setVertical(p.vertical || '');
      setBusinessName(p.name || '');
      setCountry(p.country || 'Mexico');
      setTimezone(p.timezone || 'America/Mexico_City');
      setCurrency(p.currency || 'MXN');
      setDescription(p.description || '');
      setPhone(p.phone || '');
      setEmail(p.email || '');
      setAddress(p.address || '');
      setVerticalConfig(p.vertical_config || {});
      if (p.onboarding_step) setStep(Math.min(p.onboarding_step + 1, 3));
      if (p.vertical_fields?.suggested_fields) setVerticalFields(p.vertical_fields.suggested_fields);
    }).catch(() => {});
    api.get('/verticals').then(r => setVerticals(r.data)).catch(() => {
      // Fallback: use admin verticals
      api.get('/admin/verticals').then(r => setVerticals(r.data)).catch(() => {});
    });
  }, []);

  useEffect(() => {
    if (vertical) {
      api.get('/tenant/vertical-fields').then(r => {
        setVerticalFields(r.data?.suggested_fields || []);
      }).catch(() => {});
    }
  }, [vertical]);

  const saveStep = async (stepNum, data) => {
    setSaving(true);
    setError('');
    try {
      await api.post('/tenant/onboarding/step', { step: stepNum, data });
      return true;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    let data = {};
    if (step === 1) {
      if (!businessName) { setError('El nombre del negocio es obligatorio'); return; }
      if (!timezone) { setError('La zona horaria es obligatoria'); return; }
      data = { name: businessName, description, country, timezone, locale: 'es-MX', currency, vertical, subtype: subtype || undefined };
    } else if (step === 2) {
      if (vertical === 'real_estate' && !verticalConfig.business_profile) {
        setError('Selecciona si tu negocio es Desarrollador inmobiliario o Inmobiliaria/broker'); return;
      }
      if (vertical === 'automotive' && !verticalConfig.automotive_business_type) {
        setError('Selecciona el tipo de negocio automotriz'); return;
      }
      data = { phone, email, address, vertical_config: verticalConfig };
    } else if (step === 3) {
      data = { location: { name: locName || 'Principal', address: locAddress, timezone: locTimezone || timezone, phone: locPhone, email: locEmail } };
    }

    const ok = await saveStep(step, data);
    if (!ok) return;
    // Sede (3) is now the last step of this wizard — completing it moves
    // straight into /tenant/setup, continuing the SAME shared stepper at
    // "WhatsApp" instead of pausing on a static "Todo listo" screen first.
    if (step < 3) setStep(step + 1);
    else await handleComplete();
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await api.post('/tenant/onboarding/complete');
      await checkAuth();
      navigate('/tenant/setup');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (step === 2) setStep(3);
    else if (step === 3) handleComplete();
  };

  const renderVerticalConfigField = (field) => {
    const val = verticalConfig[field.key] || (field.type === 'boolean' ? false : field.type === 'multi_select' ? [] : '');

    if (field.type === 'boolean') {
      return (
        <div key={field.key} className="flex items-center justify-between py-2">
          <Label className="text-sm text-[#0F172A]">{field.label}</Label>
          <Switch
            checked={!!val}
            onCheckedChange={(v) => setVerticalConfig({ ...verticalConfig, [field.key]: v })}
            data-testid={`vc-${field.key}`}
          />
        </div>
      );
    }
    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{field.label}</Label>
          <Select value={val || ''} onValueChange={(v) => setVerticalConfig({ ...verticalConfig, [field.key]: v })}>
            <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid={`vc-${field.key}`}>
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (field.type === 'multi_select') {
      const selected = Array.isArray(val) ? val : [];
      return (
        <div key={field.key} className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{field.label}</Label>
          <div className="flex flex-wrap gap-2">
            {field.options?.map(o => (
              <button
                key={o}
                type="button"
                onClick={() => {
                  const newVal = selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o];
                  setVerticalConfig({ ...verticalConfig, [field.key]: newVal });
                }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  selected.includes(o)
                    ? 'bg-[#F72585] text-white border-[#F72585]'
                    : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1]'
                }`}
                data-testid={`vc-${field.key}-${o}`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === 'number') {
      return (
        <div key={field.key} className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{field.label}</Label>
          <Input
            type="number"
            value={val || ''}
            onChange={(e) => setVerticalConfig({ ...verticalConfig, [field.key]: parseInt(e.target.value) || 0 })}
            className="bg-white border-[#E2E8F0] text-sm"
            data-testid={`vc-${field.key}`}
          />
        </div>
      );
    }
    return (
      <div key={field.key} className="space-y-2">
        <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">{field.label}</Label>
        <Input
          value={val || ''}
          onChange={(e) => setVerticalConfig({ ...verticalConfig, [field.key]: e.target.value })}
          className="bg-white border-[#E2E8F0] text-sm"
          data-testid={`vc-${field.key}`}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight text-center mb-6">
          Configura tu negocio
        </h1>

        <OnboardingStepper currentKey={STEP_KEYS[step]} />

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="onboarding-error">
            {error}
          </div>
        )}

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 md:p-8 animate-fade-in-up">
          {/* Step 1: Vertical + Basic Info */}
          {step === 1 && (
            <div className="space-y-5" data-testid="onboarding-step-1">
              <h2 className="text-xl font-bold text-[#0F172A] tracking-tight">Tu negocio</h2>
              <p className="text-sm text-[#64748B]">Cuéntanos sobre tu tipo de negocio</p>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre del negocio</Label>
                <Input value={businessName} onChange={e => setBusinessName(e.target.value)} className="bg-white border-[#E2E8F0] text-sm h-11" placeholder="Ej: Mi Salón de Belleza" data-testid="ob-business-name" required />
              </div>

              {verticals.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Tipo de negocio (vertical)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {verticals.map(v => {
                      const Icon = VERTICAL_ICONS[v.key] || Globe;
                      return (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => { setVertical(v.key); setSubtype(''); }}
                          className={`p-3 rounded-xl border text-center transition-all active:scale-95 ${
                            vertical === v.key
                              ? 'bg-[#F72585]/10 border-[#F72585] text-[#F72585]'
                              : 'bg-white border-[#E2E8F0] text-[#64748B] hover:border-[#CBD5E1]'
                          }`}
                          data-testid={`ob-vertical-${v.key}`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" strokeWidth={1.5} />
                          <p className="text-[11px] sm:text-xs font-medium leading-tight">{v.name}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 2026-07-24 — tipificación de sub-vertical: verticals.py ya
                  definía este array `subtypes` por vertical desde hace
                  varios sprints, pero nunca se capturaba por tenant — se
                  usaba solo como descripción, sin persistirse. Solo para
                  consulta/BI a futuro, no gatea ningún comportamiento del
                  sistema hoy. */}
              {vertical && (verticals.find(v => v.key === vertical)?.subtypes?.length > 0) && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">¿Cuál describe mejor tu negocio? (opcional)</Label>
                  <select
                    value={subtype}
                    onChange={e => setSubtype(e.target.value)}
                    className="w-full h-11 rounded-lg border border-[#E2E8F0] bg-white text-sm px-3"
                    data-testid="ob-subtype"
                  >
                    <option value="">Elegir…</option>
                    {verticals.find(v => v.key === vertical).subtypes.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">País</Label>
                  <Input value={country} onChange={e => setCountry(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" data-testid="ob-country" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Zona horaria</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="ob-timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Descripción breve (opcional)</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} className="bg-white border-[#E2E8F0] text-sm min-h-[70px]" placeholder="Describe tu negocio en una frase..." data-testid="ob-description" />
              </div>
            </div>
          )}

          {/* Step 2: Business Details */}
          {step === 2 && (
            <div className="space-y-5" data-testid="onboarding-step-2">
              <h2 className="text-xl font-bold text-[#0F172A] tracking-tight">Datos del negocio</h2>
              <p className="text-sm text-[#64748B]">Información de contacto y detalles específicos</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Teléfono</Label>
                  <PhoneInput value={phone} onChange={e => setPhone(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" placeholder="+52 55 1234 5678" data-testid="ob-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email del negocio</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" placeholder="contacto@negocio.com" data-testid="ob-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Dirección</Label>
                <Input value={address} onChange={e => setAddress(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" placeholder="Calle, número, colonia, ciudad" data-testid="ob-address" />
              </div>
              <p className="text-xs text-[#94A3B8]">
                RFC y sitio web son opcionales — puedes agregarlos después en Mi Negocio → Configuración.
              </p>

              {/* Sprint 75 (6/N) — este perfil gatea módulos completos (p.ej.
                  Desarrollos/ERP solo si business_profile=='developer'), así
                  que se pide una sola vez aquí, junto con la vertical, y
                  queda bloqueado después (ver BusinessVerticalProfile.js) —
                  nunca un botón suelto que alguien pueda tocar por accidente
                  más adelante y perder configuración ya creada. */}
              {vertical === 'real_estate' && (
                <div className="border-t border-[#E2E8F0] pt-5 mt-5 space-y-3">
                  <h3 className="text-sm font-semibold text-[#0F172A]">Perfil inmobiliario</h3>
                  <p className="text-xs text-[#94A3B8]">Esta elección no se podrá cambiar después por seguridad de tu configuración.</p>
                  <div className="grid gap-2">
                    {[{ value: 'developer', label: 'Desarrollador inmobiliario' }, { value: 'broker', label: 'Inmobiliaria / broker' }].map(t => (
                      <button key={t.value} type="button"
                        onClick={() => setVerticalConfig({ ...verticalConfig, business_profile: t.value })}
                        className={`p-3 rounded-xl border text-left text-sm transition-all ${
                          verticalConfig.business_profile === t.value ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}
                        data-testid={`ob-re-profile-${t.value}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {vertical === 'automotive' && (
                <div className="border-t border-[#E2E8F0] pt-5 mt-5 space-y-3">
                  <h3 className="text-sm font-semibold text-[#0F172A]">Tipo de negocio automotriz</h3>
                  <p className="text-xs text-[#94A3B8]">Esta elección no se podrá cambiar después por seguridad de tu configuración.</p>
                  <div className="grid gap-2">
                    {[
                      { value: 'concesionario_nuevos_y_seminuevos', label: 'Concesionario (nuevos y seminuevos)' },
                      { value: 'agencia_solo_seminuevos', label: 'Agencia (solo seminuevos)' },
                      { value: 'concesionario_motos', label: 'Concesionario de motocicletas' },
                      { value: 'taller_postventa', label: 'Taller postventa' },
                    ].map(t => (
                      <button key={t.value} type="button"
                        onClick={() => setVerticalConfig({ ...verticalConfig, automotive_business_type: t.value })}
                        className={`p-3 rounded-xl border text-left text-sm transition-all ${
                          verticalConfig.automotive_business_type === t.value ? 'border-[#0F172A] bg-[#F8FAFC] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`}
                        data-testid={`ob-auto-type-${t.value}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {verticalFields.length > 0 && (
                <div className="border-t border-[#E2E8F0] pt-5 mt-5">
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Configuración de tu vertical</h3>
                  <div className="space-y-4">
                    {verticalFields.map(f => renderVerticalConfigField(f))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: First Location */}
          {step === 3 && (
            <div className="space-y-5" data-testid="onboarding-step-3">
              <h2 className="text-xl font-bold text-[#0F172A] tracking-tight">Tu primera sede</h2>
              <p className="text-sm text-[#64748B]">Configura la ubicación principal de tu negocio</p>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre de la sede</Label>
                <Input value={locName} onChange={e => setLocName(e.target.value)} className="bg-white border-[#E2E8F0] text-sm h-11" placeholder="Ej: Sede Central, Sucursal Norte..." data-testid="ob-loc-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Dirección</Label>
                <Input value={locAddress} onChange={e => setLocAddress(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" placeholder="Dirección completa" data-testid="ob-loc-address" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Teléfono</Label>
                  <PhoneInput value={locPhone} onChange={e => setLocPhone(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" data-testid="ob-loc-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email</Label>
                  <Input type="email" value={locEmail} onChange={e => setLocEmail(e.target.value)} className="bg-white border-[#E2E8F0] text-sm" data-testid="ob-loc-email" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Zona horaria de la sede</Label>
                <Select value={locTimezone || timezone} onValueChange={setLocTimezone}>
                  <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="ob-loc-timezone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="border-[#E2E8F0] text-[#64748B]" data-testid="ob-prev-step">
                <ArrowLeft className="w-4 h-4 mr-2" /> Anterior
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {step > 1 && (
              <Button variant="ghost" onClick={handleSkip} disabled={saving} className="text-[#64748B]" data-testid="ob-skip-step">
                Omitir
              </Button>
            )}
            {step < 3 ? (
              <Button onClick={handleNext} disabled={saving} className="bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="ob-next-step">
                {saving ? 'Guardando...' : <>Siguiente <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={saving} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="ob-complete">
                {saving ? 'Finalizando...' : <>Conectar WhatsApp e IA <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
